import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/MainLayout';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { FiLink, FiAlertTriangle, FiCheckCircle, FiClock, FiDroplet, FiUserCheck, FiImage, FiType, FiPaperclip, FiInfo, FiLoader, FiX } from 'react-icons/fi';
import { FaSyringe, FaTint, FaSmoking, FaHamburger, FaGlassWhiskey, FaBed, FaGrinBeamSweat } from 'react-icons/fa';
import { PencilSimple } from '@phosphor-icons/react';
import AnalysisResultModal from '../components/AnalysisResultModal';
import LoadingOverlay from '../components/LoadingOverlay';
import { workerFetch } from '../utils/apiUtils';
import { useScrollRestoration, ensureScrollable } from '../utils/scrollUtils';

// --- Constants for DermoFit Questions ---
const SKIN_TYPES = [
  { id: 'oily', title: 'پوست چرب', description: 'پوستی که اغلب براق است و مستعد جوش می‌باشد', emoji: '✨' },
  { id: 'dry', title: 'پوست خشک', description: 'احساس کشیدگی و پوسته‌پوسته شدن دارد', emoji: '🌵' },
  { id: 'combination', title: 'پوست مختلط', description: 'در ناحیه T چرب و در بقیه قسمت‌ها خشک است', emoji: '🌗' },
  { id: 'normal', title: 'پوست نرمال', description: 'متعادل، بدون چربی یا خشکی محسوس', emoji: '😊' },
];

const ALLERGY_OPTIONS = [
  'پارابن', 'عطر', 'رتینول', 'سولفات', 'الکل', 'رنگ مصنوعی'
];

const PRODUCT_CATEGORIES = [
  'ضدآفتاب', 'مرطوب‌کننده', 'شوینده صورت', 'کرم شب', 'سرم', 'تونر', 'ماسک', 'لایه بردار'
];

// Worker URL (Copied from ProfilePage)
const WORKER_URL = process.env.REACT_APP_API_BASE_URL || 'https://empty-rain-2654.er-hooman.workers.dev';

const ProfitScanPage = () => {
  const { setHeaderTitle } = useOutletContext();
  const navigate = useNavigate();
  const { userId, token } = useAuth();

  // --- State for Validation Flow ---
  const [validationStatus, setValidationStatus] = useState('idle'); // idle, checking, profileIncomplete, scanMissing, dermofitMissing, dermofitOnboarding, allComplete, submittingAnalysis, error
  const [loadingMessage, setLoadingMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  
  // --- State for DermoFit Modal ---
  const [showDermofitModal, setShowDermofitModal] = useState(false);
  const [modalStep, setModalStep] = useState(0);
  const [dermofitData, setDermofitData] = useState({
    skinType: null,
    allergies: [],
    otherAllergy: '',
    currentProducts: PRODUCT_CATEGORIES.reduce((acc, category) => {
      acc[category] = '';
      return acc;
    }, {}),
  });
  const [isSubmittingModal, setIsSubmittingModal] = useState(false);

  // --- State for Product Input & Analysis ---
  const [inputMode, setInputMode] = useState(null);
  const [productNameInput, setProductNameInput] = useState('');
  const [productLinkInput, setProductLinkInput] = useState('');
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState(null);
  const [isSubmittingAnalysis, setIsSubmittingAnalysis] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  // --- State for Previous Analyses ---
  const [previousAnalyses, setPreviousAnalyses] = useState([]);
  const [loadingPreviousAnalyses, setLoadingPreviousAnalyses] = useState(false);
  const [selectedAnalysisInputInfo, setSelectedAnalysisInputInfo] = useState(null);
  const [isLoadingAnalysisDetails, setIsLoadingAnalysisDetails] = useState(false);
  const [isLoadingDermofitEdit, setIsLoadingDermofitEdit] = useState(false);
  
  // Enable scroll restoration
  useScrollRestoration();
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisModalError, setAnalysisModalError] = useState(null);

  // Ref to scroll to the input section on mobile
  const inputSectionRef = useRef(null);

  // Scroll the input section into view when inputMode changes
  useEffect(() => {
    if (inputMode && inputSectionRef.current) {
      inputSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [inputMode]);

  // Set Header Title
  useEffect(() => {
    return () => setHeaderTitle("");
  }, [setHeaderTitle]);

  // --- NEW: Fetch Initialization Data from Worker ---
  const initializeProfitScan = useCallback(async () => {
    if (!userId || !token) {
      setValidationStatus('error');
      setErrorMessage('کاربر شناسایی نشد. لطفاً دوباره وارد شوید.');
      return;
    }

    setValidationStatus('checking');
    setLoadingMessage('در حال بررسی وضعیت و بارگذاری اطلاعات...');
    setErrorMessage(null);
    setPreviousAnalyses([]);
    setLoadingPreviousAnalyses(true);

    try {
      const data = await workerFetch('/api/profitscan/init', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (data && data.status) {
        console.log("ProfitScan Init Response:", data);
        setValidationStatus(data.status);
        setPreviousAnalyses(data.previousAnalyses || []);
        if (data.status === 'profileIncomplete') setLoadingMessage('پروفایل شما ناقص است.');
        else if (data.status === 'scanMissing') setLoadingMessage('نیاز به اسکن پوست.');
        else if (data.status === 'dermofitMissing') setLoadingMessage('پروفایل درموفیت را تکمیل کنید.');
        else setLoadingMessage('');

      } else {
        console.error("ProfitScan Init Error: Invalid response", data);
        setValidationStatus('error');
        setErrorMessage(data?.message || 'خطا در بارگذاری اطلاعات');
      }
    } catch (err) {
      console.error("ProfitScan Init Network Error:", err);
      setValidationStatus('error');
      setErrorMessage('خطای شبکه در ارتباط با سرور.');
    } finally {
      setLoadingPreviousAnalyses(false);
      setValidationStatus(prevStatus => (prevStatus === 'checking' ? 'error' : prevStatus));
      if (validationStatus === 'checking' && !errorMessage) {
           setErrorMessage('وضعیت نامشخص پس از بررسی اولیه.');
      }
    }
  }, [userId, token]);

  // --- Main Validation & Data Fetching Logic Hook (Uses the new function) ---
  useEffect(() => {
    if (userId && token) {
       initializeProfitScan();
    } else {
       setValidationStatus('checking');
       setLoadingMessage('در انتظار اطلاعات کاربر...');
    }
  }, [userId, token, initializeProfitScan]);

  // --- DermoFit Modal Input Handlers ---
  const handleSkinTypeSelect = (typeId) => {
    setDermofitData(prev => ({ ...prev, skinType: typeId }));
  };

  const handleAllergyToggle = (allergy) => {
    setDermofitData(prev => {
      const newAllergies = prev.allergies.includes(allergy)
        ? prev.allergies.filter(a => a !== allergy)
        : [...prev.allergies, allergy];
      return { ...prev, allergies: newAllergies };
    });
  };

  const handleOtherAllergyChange = (event) => {
    setDermofitData(prev => ({ ...prev, otherAllergy: event.target.value }));
  };

  const handleProductInputChange = (category, value) => {
    setDermofitData(prev => ({
      ...prev,
      currentProducts: {
        ...prev.currentProducts,
        [category]: value,
      },
    }));
  };

  // --- NEW: DermoFit Submission Logic (Uses Worker API) ---
  const handleDermofitSubmit = async () => {
    if (!userId) {
      setErrorMessage("کاربر شناسایی نشد.");
      return;
    }
    if (!dermofitData.skinType) {
      alert("لطفاً نوع پوست خود را انتخاب کنید.");
      setModalStep(0);
      return;
    }

    setIsSubmittingModal(true);
    setErrorMessage(null);

    try {
      const response = await workerFetch('/api/profitscan/dermofit', {
        method: 'POST',
        body: JSON.stringify(dermofitData),
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        console.log("DermoFit profile saved successfully via Worker!");
        setShowDermofitModal(false);
        await initializeProfitScan();
      } else {
        const errorData = await response.json().catch(() => ({ message: `خطا در ذخیره سازی پروفایل درموفیت (${response.status})` }));
        console.error("Error saving DermoFit profile via Worker:", response.status, errorData);
                  setErrorMessage(errorData.message || "خطا در ذخیره سازی پروفایل درموفیت.");
      }
    } catch (err) {
      console.error("Network error saving DermoFit profile via Worker:", err);
              setErrorMessage("خطای شبکه در ذخیره سازی پروفایل درموفیت.");
    } finally {
      setIsSubmittingModal(false);
    }
  };

  // Add this new function for compressing and converting image to Base64
  const compressImageFile = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxDimension = 1024;
      let width = img.width;
      let height = img.height;
      if (width > height && width > maxDimension) {
        height = (height * maxDimension) / width;
        width = maxDimension;
      } else if (height > maxDimension) {
        width = (width * maxDimension) / height;
        height = maxDimension;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7); // compress to JPEG at 70% quality
      resolve(dataUrl);
    };
    img.onerror = (error) => reject(error);
    img.src = URL.createObjectURL(file);
  });

  // Keep handleImageFileChange for preview
  const handleImageFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
          setProductImageFile(file);
          const previewUrl = URL.createObjectURL(file);
          setProductImagePreview(previewUrl);
          return () => URL.revokeObjectURL(previewUrl);
      }
  };

  // --- NEW: Product Analysis Submission (Uses Worker API) ---
  const handleSubmitProductAnalysis = async () => {
    let dataForApi = null;
    let currentInputMode = inputMode;

    setErrorMessage(null);
    setAnalysisResult(null);

    if (currentInputMode === 'image') {
      if (!productImageFile) {
        setErrorMessage("لطفا یک تصویر انتخاب کنید.");
        return;
      }
      try {
        setIsSubmittingAnalysis(true);
        setLoadingMessage("در حال پردازش تصویر...");
        // compress and convert image to Base64 to reduce payload size
        dataForApi = await compressImageFile(productImageFile);
      } catch (error) {
        console.error("Error converting image:", error);
        setErrorMessage("خطا در پردازش تصویر.");
        setIsSubmittingAnalysis(false);
        return;
      }
    } else if (currentInputMode === 'name') {
      if (!productNameInput.trim()) {
        setErrorMessage("لطفا نام محصول را وارد کنید.");
        return;
      }
      dataForApi = productNameInput.trim();
    } else if (currentInputMode === 'link') {
      if (!productLinkInput.trim()) {
        setErrorMessage("لطفا لینک محصول را وارد کنید.");
        return;
      }
      try {
        new URL(productLinkInput.trim());
        dataForApi = productLinkInput.trim();
      } catch (_) {
        setErrorMessage("لینک وارد شده معتبر نیست.");
        return;
      }
    } else {
      setErrorMessage("لطفا یک روش ورودی (عکس، نام یا لینک) را انتخاب کنید.");
      return;
    }

    setIsSubmittingAnalysis(true);
    setLoadingMessage("در حال تحلیل محصول توسط هوش مصنوعی...");
    setValidationStatus('submittingAnalysis');

    try {
      const response = await workerFetch('/api/profitscan/analyze', {
        method: 'POST',
        body: JSON.stringify({
          inputMode: currentInputMode,
          inputValue: dataForApi,
        }),
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // Handle 502 Bad Gateway specifically with friendly message
      if (response.status === 502) {
        // Show a user-friendly retry message
        setErrorMessage('خطایی رخ نداده است. لطفاً مجدداً اسکن کنید.');
        setIsSubmittingAnalysis(false);
        setLoadingMessage('');
        setValidationStatus('allComplete');
        return;
      }

      if (response.ok) {
        const result = await response.json();
        console.log("Analysis Response from Worker:", result);
        if (result && result.analysisResult) {
          setAnalysisResult(result.analysisResult);
          setSelectedAnalysisInputInfo({
             method: currentInputMode,
             value: currentInputMode === 'image' ? 'تصویر آپلود شده' : (currentInputMode === 'link' ? productLinkInput : productNameInput)
          });
          setAnalysisModalError(null);
          setShowAnalysisModal(true);
          await initializeProfitScan();

          setInputMode(null);
          setProductNameInput('');
          setProductLinkInput('');
          setProductImageFile(null);
          setProductImagePreview(null);

        } else {
           throw new Error("پاسخ نامعتبر از سرویس تحلیل دریافت شد.");
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: `خطا در تحلیل (${response.status})` }));
        console.error("Error calling analysis Worker API:", response.status, errorData);
        throw new Error(errorData.message || `خطا در تحلیل محصول (${response.status})`);
      }

    } catch (error) {
      console.error("Error during analysis submission:", error);
      setErrorMessage(error.message || "خطا در ارتباط با سرویس تحلیل.");
    } finally {
      setIsSubmittingAnalysis(false);
      setLoadingMessage('');
      setValidationStatus('allComplete');
    }
  };

  // --- NEW: Click Handler for Previous Analysis (Opens Modal) ---
  const handlePreviousAnalysisClick = async (analysisSummary) => {
    console.log("Clicked previous analysis summary:", analysisSummary);
    if (!analysisSummary || !analysisSummary.id) {
        console.error("Invalid analysis summary clicked");
        return;
    }

    setIsLoadingAnalysisDetails(true);
    setAnalysisModalError(null);
    setAnalysisResult(null);
    setSelectedAnalysisInputInfo(null);
    setShowAnalysisModal(true);

    try {
        const response = await workerFetch('/api/profitscan/analysis/' + analysisSummary.id, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
            const fullAnalysisData = await response.json();
            console.log("Fetched full analysis details:", fullAnalysisData);
            if (fullAnalysisData && fullAnalysisData.analysisResult) {
                setAnalysisResult(fullAnalysisData.analysisResult);
                setSelectedAnalysisInputInfo({
                    method: fullAnalysisData.inputMethod,
                    value: fullAnalysisData.inputValue
                });
            } else {
                 console.error("Incomplete analysis data received from API");
                 setAnalysisModalError("اطلاعات کامل تحلیل قبلی دریافت نشد.");
            }
        } else {
            const errorData = await response.json().catch(() => ({ message: `خطا در دریافت جزئیات (${response.status})` }));
            console.error("Error fetching analysis details:", response.status, errorData);
            setAnalysisModalError(errorData.message || `خطا در دریافت جزئیات تحلیل (${response.status})`);
        }
    } catch (error) {
        console.error("Error fetching previous analysis details:", error);
        setAnalysisModalError(error.message || "خطای شبکه در بارگذاری جزئیات تحلیل قبلی.");
    } finally {
        setIsLoadingAnalysisDetails(false);
    }
  };

  // --- Edit DermoFit Click Handler (Fetches data before opening) ---
  const handleEditDermofitClick = async () => {
      setIsLoadingDermofitEdit(true);
      setErrorMessage(null);
      console.log("Fetching existing DermoFit data for editing...");
      try {
          const response = await workerFetch('/api/profitscan/dermofit', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          let initialData = {
                skinType: null,
                allergies: [],
                otherAllergy: '',
                currentProducts: PRODUCT_CATEGORIES.reduce((acc, category) => ({ ...acc, [category]: '' }), {}),
            };

          if (response.ok) {
              const result = await response.json();
              if (result.found && result.data) {
                  console.log("Found existing DermoFit data:", result.data);
                  initialData = {
                      ...initialData,
                      ...result.data,
                      currentProducts: {
                          ...initialData.currentProducts,
                          ...(result.data.currentProducts || {})
                      }
                  };
              } else {
                  console.log("No existing DermoFit data found, opening with defaults.");
              }
          } else {
               console.error("Error fetching DermoFit profile:", response.status);
               setErrorMessage("خطا در بارگذاری اطلاعات قبلی پروفایل درموفیت.");
          }
          
          setDermofitData(initialData);
          setModalStep(0);
          setShowDermofitModal(true);

      } catch (err) {
          console.error("Network error fetching DermoFit profile for edit:", err);
          setErrorMessage("خطای شبکه هنگام بارگذاری پروفایل درموفیت.");
      } finally {
          setIsLoadingDermofitEdit(false);
      }
  };

  // --- Helper Functions (Keep as is) ---
  const getCompatibilityStyles = (percentage) => {
      const numPercentage = Number(percentage);
      if (isNaN(numPercentage)) {
          return { pathColor: '#9CA3AF', textColor: '#6B7280', trailColor: '#E5E7EB', label: 'نامشخص' };
      }
      if (numPercentage >= 80) return { pathColor: '#10B981', textColor: '#059669', trailColor: '#D1FAE5', label: 'عالی' };
      if (numPercentage >= 50) return { pathColor: '#F59E0B', textColor: '#D97706', trailColor: '#FEF3C7', label: 'متوسط' };
      return { pathColor: '#EF4444', textColor: '#DC2626', trailColor: '#FEE2E2', label: 'ناسازگار' };
  };

  const getCompatibilityText = (percentage) => {
       const numPercentage = Number(percentage);
      if (isNaN(numPercentage)) return "میزان سازگاری نامشخص است.";
      if (numPercentage >= 80) return "این محصول با شرایط پوست شما کاملاً سازگار است.";
      if (numPercentage >= 50) return "این محصول با شرایط پوست شما تا حد زیادی سازگار است.";
      return "این محصول ممکن است با پوست شما سازگار نباشد. قبل از استفاده، توصیه می‌شود با پزشک یا متخصص مشورت کنید.";
  };

  // --- Render based on Validation Status (Adjustments for new flow) ---
  const renderContent = () => {
    switch (validationStatus) {
      case 'idle':
      case 'checking':
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] p-6 text-center">
                <FiLoader size={40} className="animate-spin text-blue-500 mb-4" />
                <p className="text-lg font-medium text-gray-700">{loadingMessage || 'در حال بارگذاری و بررسی اطلاعات...'}</p>
                <p className="text-sm text-gray-500 mt-1">لطفاً کمی صبر کنید.</p>
            </div>
        );
      case 'profileIncomplete':
        return (
          <div className="flex flex-col items-center justify-center min-h-[300px] p-6 text-center">
            <FiUserCheck size={56} className="text-orange-500 mb-5"/>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">پروفایل شما ناقص است</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">برای دسترسی به تحلیل‌های درموفیت و دریافت نتایج دقیق‌تر، لطفاً اطلاعات پروفایل اصلی خود (مانند سن، جنسیت و سبک زندگی) را تکمیل کنید.</p>
            <button
              onClick={() => navigate('/profile')}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5 px-8 rounded-lg shadow-md transition duration-200"
            >
              تکمیل پروفایل کاربری
            </button>
          </div>
        );
      case 'scanMissing':
        return (
           <div className="flex flex-col items-center justify-center min-h-[300px] p-6 text-center">
            <FiImage size={56} className="text-purple-500 mb-5"/>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">نیاز به اسکن پوست</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">برای تحلیل دقیق محصولات، ابتدا باید وضعیت فعلی پوست شما از طریق درموآنالیز (اسکن پوست) مشخص شود.</p>
            <button
              onClick={() => navigate('/scanpage')}
              className="bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2.5 px-8 rounded-lg shadow-md transition duration-200"
            >
              انجام درموآنالیز (اسکن)
            </button>
          </div>
        );
      case 'dermofitMissing':
        return (
           <div className="flex flex-col items-center justify-center min-h-[300px] p-6 text-center">
            <FaTint size={56} className="text-teal-500 mb-5"/>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">پروفایل درموفیت را تکمیل کنید</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">برای اینکه هوش مصنوعی بتواند سازگاری محصولات را با نوع پوست، آلرژی‌ها و محصولات فعلی شما بسنجد، لطفاً به چند سوال پاسخ دهید.</p>
            <button
              onClick={() => {
                  handleEditDermofitClick();
              }}
              className="bg-teal-500 hover:bg-teal-600 text-white font-semibold py-2.5 px-8 rounded-lg shadow-md transition duration-200 disabled:opacity-50 flex items-center">
               {isLoadingDermofitEdit ? <FiLoader className="animate-spin mr-2" /> : null}
               شروع پروفایل درموفیت
            </button>
            {errorMessage && <p className="text-sm text-red-600 mt-4">{errorMessage}</p>}
          </div>
        );
      case 'allComplete':
        return (
          <div className="p-4 md:p-6 space-y-8">
            <div className="bg-white shadow rounded-lg p-5">
              <div className="flex justify-between items-start mb-4">
                 <div>
                   <h2 className="text-xl font-semibold text-gray-800 mb-1">شروع تحلیل جدید</h2>
                   <p className="text-sm text-gray-600">عکس، لینک، یا نام محصول مورد نظر خود را برای بررسی سازگاری با پوستتان وارد کنید.</p>
                 </div>
                 <button
                    onClick={handleEditDermofitClick}
                    disabled={isLoadingDermofitEdit}
                    className="flex items-center space-x-1 rtl:space-x-reverse text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md transition duration-150 whitespace-nowrap disabled:opacity-50 disabled:cursor-wait"
                 >
                     {isLoadingDermofitEdit ? (
                         <FiLoader className="animate-spin w-4 h-4" />
                     ) : (
                         <PencilSimple size={14} />
                     )}
                     <span>ویرایش درموفیت</span>
                 </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                 <button
                    onClick={() => setInputMode('image')}
                    className={`p-4 rounded-xl border transition-all duration-200 flex flex-col items-center text-center ${inputMode === 'image' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 shadow-md' : 'bg-white hover:shadow-md hover:border-gray-300'}`}
                 >
                    <FiImage size={32} className={`mb-2 ${inputMode === 'image' ? 'text-blue-600' : 'text-green-500'}`}/>
                    <span className="font-semibold">عکس محصول</span>
                    <span className="text-xs text-gray-500 mt-1">آپلود / گرفتن عکس</span>
                 </button>
                 <button
                    onClick={() => setInputMode('link')}
                    className={`p-4 rounded-xl border transition-all duration-200 flex flex-col items-center text-center ${inputMode === 'link' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 shadow-md' : 'bg-white hover:shadow-md hover:border-gray-300'}`}
                 >
                    <FiLink size={32} className={`mb-2 ${inputMode === 'link' ? 'text-blue-600' : 'text-indigo-500'}`}/>
                    <span className="font-semibold">لینک محصول</span>
                    <span className="text-xs text-gray-500 mt-1">وارد کردن آدرس</span>
                 </button>
                 <button
                    onClick={() => setInputMode('name')}
                    className={`p-4 rounded-xl border transition-all duration-200 flex flex-col items-center text-center ${inputMode === 'name' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 shadow-md' : 'bg-white hover:shadow-md hover:border-gray-300'}`}
                 >
                     <FiType size={32} className={`mb-2 ${inputMode === 'name' ? 'text-blue-600' : 'text-purple-500'}`}/>
                     <span className="font-semibold">نام محصول</span>
                     <span className="text-xs text-gray-500 mt-1">تایپ نام دقیق</span>
                 </button>
              </div>

              {/* Conditional Input Fields with Slide/Fade Animation */} 
              <div
                ref={inputSectionRef}
                className={`transition-all duration-300 ease-out overflow-hidden ${inputMode ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}
              >
                {/* Inner container for padding and background */} 
                <div className="p-4 border rounded-lg bg-gray-50 shadow-inner">
                  {inputMode === 'image' && (
                    <div className="space-y-3">
                        <label htmlFor="productImageUpload" className="block text-sm font-medium text-gray-700 mb-1">فایل تصویر محصول:</label>
                        <input type="file" id="productImageUpload" accept="image/*" capture="environment" onChange={handleImageFileChange} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"/>
                        {productImagePreview && <div className="mt-3 text-center"><img src={productImagePreview} alt="پیش‌نمایش محصول" className="max-h-48 rounded-lg mx-auto shadow-sm inline-block"/></div>}
                    </div>
                  )}
                  {inputMode === 'link' && (
                    <div>
                        <label htmlFor="productLinkInput" className="block text-sm font-medium text-gray-700 mb-1">لینک خرید یا مشخصات محصول:</label>
                        <input type="url" id="productLinkInput" value={productLinkInput} onChange={(e) => setProductLinkInput(e.target.value)} placeholder="https://example.com/product-page" className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"/>
                     </div>
                  )}
                  {inputMode === 'name' && (
                    <div>
                        <label htmlFor="productNameInput" className="block text-sm font-medium text-gray-700 mb-1">نام کامل محصول:</label>
                        <input type="text" id="productNameInput" value={productNameInput} onChange={(e) => setProductNameInput(e.target.value)} placeholder="مثال: کرم ضد آفتاب لاروش پوزای مدل Anthelios UVMUNE" className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"/>
                    </div>
                  )}
                </div>
              </div>

              {inputMode && (
                <div className="mt-6 text-center">
                  <button
                    onClick={handleSubmitProductAnalysis}
                    disabled={isSubmittingAnalysis}
                    className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2.5 px-8 rounded-lg shadow-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center w-full sm:w-auto mx-auto"
                  >
                    {isSubmittingAnalysis ? (
                        <><FiLoader className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" /> در حال تحلیل...</>
                      ) : ( 'شروع تحلیل محصول' )}
                  </button>
                  {errorMessage && validationStatus === 'allComplete' && (
                    <p className="text-sm text-red-600 text-center mt-3">{errorMessage}</p>
                  )}
                </div>
              )}
            </div>

            {(previousAnalyses.length > 0 || loadingPreviousAnalyses) &&
               <div className="border-t border-gray-200 pt-8">
                 {loadingPreviousAnalyses ? (
                   <div className="text-center text-gray-500 my-4">
                     <FiLoader className="animate-spin inline-block mr-2" /> در حال بارگذاری تحلیل‌های قبلی...
                   </div>
                 ) : previousAnalyses.length > 0 ? (
                   <div className="space-y-4">
                     <div className="text-center">
                          <h2 className="text-lg font-semibold text-gray-800 mb-1">تحلیل‌های اخیر شما</h2>
                          <p className="text-xs text-gray-500">برای مشاهده نتیجه، روی کارت مورد نظر کلیک کنید.</p>
                     </div>
                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                       {previousAnalyses.map((analysis) => (
                        <button
                          key={analysis.id}
                          onClick={() => handlePreviousAnalysisClick(analysis)}
                          disabled={isLoadingAnalysisDetails && showAnalysisModal}
                          className="group p-4 border rounded-xl bg-white hover:shadow-lg hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-all duration-200 text-right flex items-center space-x-3 rtl:space-x-reverse shadow-sm w-full disabled:opacity-60 disabled:cursor-wait transform hover:-translate-y-1"
                        >
                          <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-xl text-white ${analysis.inputMethod === 'image' ? 'bg-green-500' : analysis.inputMethod === 'link' ? 'bg-indigo-500' : 'bg-purple-500'}`}>
                            {analysis.inputMethod === 'image' ? <FiImage /> : analysis.inputMethod === 'link' ? <FiLink /> : <FiType />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 group-hover:text-blue-600 transition-colors truncate" title={analysis.productName || 'تحلیل قبلی'}>
                              {analysis.productName || 'تحلیل قبلی'}
                            </p>
                            <p className="text-xs text-gray-400 group-hover:text-gray-500 transition-colors mt-1">
                              {analysis.createdAt ? new Date(analysis.createdAt).toLocaleDateString('fa-IR', { day: 'numeric', month: 'long' }) : ''}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                   </div>
                 ) : (
                    <div className="text-center text-gray-500 my-4 p-4 bg-gray-50 rounded-lg border">
                       <p className="mb-1 text-sm">هنوز هیچ تحلیلی انجام نداده‌اید.</p>
                    </div>
                 )}
               </div>
             }
          </div>
        );
      case 'submittingAnalysis':
        return (
            <div className="p-6 text-center flex flex-col items-center justify-center min-h-[300px]">
                <FiLoader size={40} className="animate-spin text-blue-500 mb-4" />
                <p className="text-gray-600">{loadingMessage || 'در حال تحلیل محصول توسط هوش مصنوعی...'}</p>
                <p className="text-sm text-gray-500 mt-2">این فرآیند ممکن است چند لحظه طول بکشد.</p>
            </div>
        );
      case 'error':
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] p-6 text-center bg-red-50 border border-red-200 rounded-lg m-4">
                 <FiAlertTriangle className="w-12 h-12 text-red-500 mb-4" />
                 <h2 className="text-xl font-semibold text-red-700 mb-2">خطا در بارگذاری</h2>
                 <p className="text-red-600">متاسفانه هنگام پردازش درخواست شما خطایی رخ داده است.</p>
                 {errorMessage && <p className="text-sm text-red-500 mt-2 bg-red-100 px-3 py-1 rounded">جزئیات: {errorMessage}</p>}
                 <button onClick={initializeProfitScan} className="mt-5 px-4 py-2 bg-black text-white rounded-full text-sm shadow hover:bg-gray-800">تلاش مجدد</button>
            </div>
        );
      default:
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] p-6 text-center">
                <p className="text-lg font-medium text-gray-700">وضعیت نامشخص: {validationStatus}</p>
                {errorMessage && <p className="text-sm text-red-500 mt-2">{errorMessage}</p>}
                <button onClick={initializeProfitScan} className="mt-4 px-4 py-2 bg-black text-white rounded-full text-sm">بارگذاری مجدد</button>
            </div>
        );
    }
  };

  // --- DermoFit Modal Implementation (Should be defined at component scope) ---
  const renderDermofitModal = () => {
    if (!showDermofitModal) return null;

    let stepContent;
    switch (modalStep) {
        case 0: // Skin Type
            stepContent = (
                <div className="space-y-3">
                    <p className="text-center text-gray-700 mb-4">نوع پوست خود را انتخاب کنید:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{SKIN_TYPES.map(type => (<button key={type.id} onClick={() => handleSkinTypeSelect(type.id)} className={`p-4 border-2 rounded-lg text-right transition duration-200 flex items-start space-x-3 rtl:space-x-reverse ${dermofitData.skinType === type.id ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'}`}><span className="text-2xl mt-1">{type.emoji}</span><div><span className="font-semibold block text-sm">{type.title}</span><span className="text-xs text-gray-600">{type.description}</span></div></button>))}</div>
                </div>
            );
            break;
        case 1: // Allergies
            stepContent = (
                <div className="space-y-4">
                    <p className="text-gray-700 mb-2 text-sm">آیا به مواد خاصی آلرژی یا حساسیت دارید؟ (اختیاری)</p>
                    <div className="flex flex-wrap gap-2">
                        {ALLERGY_OPTIONS.map(allergy => (
                            <button key={allergy} onClick={() => handleAllergyToggle(allergy)} className={`px-3 py-1.5 border rounded-full text-xs transition ${dermofitData.allergies.includes(allergy) ? 'bg-red-100 border-red-300 text-red-800 font-medium ring-1 ring-red-300' : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'}`}>
                                {dermofitData.allergies.includes(allergy) ? '✓ ' : ''}{allergy}
                            </button>
                        ))}
                    </div>
                    <div>
                        <label htmlFor="otherAllergy" className="block text-sm font-medium text-gray-700 mb-1">سایر موارد (با کاما جدا کنید):</label>
                        <input type="text" id="otherAllergy" value={dermofitData.otherAllergy} onChange={handleOtherAllergyChange} placeholder="مثال: نیاسینامید، ویتامین C" className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"/>
                    </div>
                </div>
            );
            break;
        case 2: // Current Products
            stepContent = (
                <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2 -mr-2 pl-1">
                    <p className="text-gray-700 mb-2 text-sm">در حال حاضر از چه محصولات مراقبتی استفاده می‌کنید؟ (اختیاری)</p>
                    {PRODUCT_CATEGORIES.map(category => (
                        <div key={category}>
                            <label htmlFor={`product-${category}`} className="block text-sm font-medium text-gray-700 mb-1">{category}:</label>
                            <input type="text" id={`product-${category}`} value={dermofitData.currentProducts[category]} onChange={(e) => handleProductInputChange(category, e.target.value)} placeholder="نام برند و مدل..." className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"/>
                        </div>
                    ))}
                </div>
            );
            break;
        default:
            stepContent = <p>مرحله نامعتبر</p>;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-40 flex items-center justify-center p-4 transition-opacity duration-300 ease-in-out" onClick={() => setShowDermofitModal(false)} dir='rtl'>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 relative transition-transform transform scale-100 duration-300 ease-in-out flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
                 <div className="flex justify-between items-center pb-3 border-b mb-4">
                     <h2 className="text-lg font-semibold">پروفایل درموفیت</h2>
                     <button onClick={() => setShowDermofitModal(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"><FiX size={20}/></button>
                 </div>
                 <div className="w-full bg-gray-200 rounded-full h-1.5 mb-5"><div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${((modalStep + 1) / 3) * 100}%`, transition: 'width 0.3s ease-in-out' }}></div></div>
                 <div className="min-h-[250px] overflow-y-auto px-1 mb-4 flex-grow">{stepContent}</div>
                 <div className="flex justify-between items-center mt-auto pt-4 border-t border-gray-200">
                    <button onClick={() => setModalStep(s => Math.max(0, s - 1))} disabled={modalStep === 0 || isSubmittingModal} className="text-gray-600 px-4 py-2 rounded disabled:opacity-50 hover:bg-gray-100 text-sm">قبلی</button>
                    {modalStep < 2 && <button onClick={() => setModalStep(s => Math.min(2, s + 1))} disabled={isSubmittingModal} className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-5 py-2 rounded-lg shadow-md transition text-sm disabled:opacity-50">بعدی</button>}
                    {modalStep === 2 &&
                        <button onClick={handleDermofitSubmit} disabled={isSubmittingModal} className="bg-green-500 hover:bg-green-600 text-white font-semibold px-5 py-2 rounded-lg shadow-md transition text-sm disabled:opacity-50 flex items-center">
                            {isSubmittingModal ? <><FiLoader className="animate-spin -ml-1 mr-2 h-5 w-5" /> ذخیره...</> : 'ذخیره پروفایل'}
                        </button>
                    }
                </div>
            </div>
        </div>
    );
  };

  return (
    <>
      <LoadingOverlay 
        isLoading={validationStatus === 'checking' || isSubmittingAnalysis || isSubmittingModal || isLoadingDermofitEdit} 
        message={
          validationStatus === 'checking' ? 'در حال بررسی وضعیت...' :
          isSubmittingAnalysis ? 'در حال تحلیل محصول...' :
          isSubmittingModal ? 'در حال ذخیره پروفایل...' :
          isLoadingDermofitEdit ? 'در حال بارگذاری پروفایل...' :
          'در حال بارگذاری...'
        }
      />
      {renderContent()}
      {renderDermofitModal()}
      <AnalysisResultModal
        isOpen={showAnalysisModal}
        onClose={() => {
            setShowAnalysisModal(false);
            setAnalysisResult(null);
            setSelectedAnalysisInputInfo(null);
            setAnalysisModalError(null);
        }}
        analysisData={analysisResult}
        inputInfo={selectedAnalysisInputInfo}
        isLoading={isLoadingAnalysisDetails}
        error={analysisModalError}
      />
    </>
  );
};

export default ProfitScanPage; 